import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, skip, switchMap, take, tap } from 'rxjs/operators';

export type MaybeId = number | null;

export type PaginationState = {
  firstPage: boolean;
  lastPage: boolean;
  currentPageData: number[] | null;
};

@Injectable()
export abstract class DialogNavigationServiceBase<
  T extends { id?: number },
  PState extends PaginationState = PaginationState
> {
  readonly isLoading$: Observable<boolean>;
  readonly selectedItem$: Observable<T | null>;
  readonly lastItem$: Observable<boolean>;
  readonly firstItem$: Observable<boolean>;
  readonly previousPage$: Observable<void>;
  readonly nextPage$: Observable<void>;

  protected readonly itemIdsSubject: BehaviorSubject<number[]>;
  protected readonly selectedItemSubject: BehaviorSubject<number>;
  protected readonly isLoadingSubject: BehaviorSubject<boolean>;
  protected readonly paginationStateSubject: BehaviorSubject<PState>;
  protected readonly getNextPageSubject: Subject<void>;
  protected readonly getPreviousPageSubject: Subject<void>;

  get selectedItemId(): MaybeId {
    return this.selectedItemSubject.value;
  }

  protected get paginationState(): PState {
    return this.paginationStateSubject.value;
  }

  constructor() {
    this.itemIdsSubject = new BehaviorSubject<number[]>(null);
    this.selectedItemSubject = new BehaviorSubject<number>(null);
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.paginationStateSubject = new BehaviorSubject<PState>(null);
    this.getNextPageSubject = new Subject<void>();
    this.getPreviousPageSubject = new Subject<void>();

    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.selectedItem$ = this.selectedItemSubject.asObservable().pipe(
      switchMap((itemId: number | null) => (itemId ? this.getById(itemId) : of(null))),
      tap(() => this.isLoadingSubject.next(false)),
      shareReplay(1)
    );

    const itemAndPagination$ = combineLatest([
      this.selectedItemSubject.asObservable(),
      this.paginationStateSubject.asObservable()
    ]);

    this.firstItem$ = itemAndPagination$.pipe(
      map(
        ([itemId, paginationState]) =>
          paginationState?.firstPage && this.getSelectedItemIndex(itemId, paginationState) === 0
      )
    );
    this.lastItem$ = itemAndPagination$.pipe(
      map(
        ([itemId, paginationState]) =>
          paginationState?.lastPage &&
          this.getSelectedItemIndex(itemId, paginationState) === paginationState.currentPageData.length - 1
      )
    );
    this.previousPage$ = this.getPreviousPageSubject.asObservable();
    this.nextPage$ = this.getNextPageSubject.asObservable();
  }

  getNextItem(): void {
    if (this.isLastIndex(this.selectedItemId, this.paginationState)) {
      if (!this.paginationState.lastPage) {
        this.getNextNavigation()
          .pipe(
            // On set la pièce sélectionnée à la première du tableau
            tap(({ currentPageData }) => this.setSelectedItem(currentPageData[0]))
          )
          .subscribe();
        this.getNextPage();
      }
      return;
    }
    const nextIndex: number = this.getSelectedItemIndex(this.selectedItemId, this.paginationState) + 1;
    if (nextIndex < this.paginationState.currentPageData.length) {
      this.isLoadingSubject.next(true);
      this.setSelectedItem(this.paginationState.currentPageData[nextIndex]);
    }
  }

  getPreviousItem(): void {
    if (this.isFirstIndex(this.selectedItemId, this.paginationState)) {
      if (!this.paginationState.firstPage) {
        this.getNextNavigation()
          .pipe(
            // On set la pièce sélectionné à la dernière du tableau
            tap(({ currentPageData }) => this.setSelectedItem(currentPageData[currentPageData.length - 1]))
          )
          .subscribe();
        this.getPreviousPage();
      }
      return;
    }

    const previousIndex: number = this.getSelectedItemIndex(this.selectedItemId, this.paginationState) - 1;
    if (previousIndex > -1) {
      this.isLoadingSubject.next(true);
      this.setSelectedItem(this.paginationState.currentPageData[previousIndex]);
    }
  }

  getNextPage(): void {
    this.isLoadingSubject.next(true);
    this.getNextPageSubject.next();
  }

  getPreviousPage(): void {
    this.isLoadingSubject.next(true);
    this.getPreviousPageSubject.next();
  }

  setSelectedItem(item: T | MaybeId): void {
    if (item !== undefined) {
      this.selectedItemSubject.next(this.toId(item));
    } else {
      this.selectedItemSubject.next(null);
    }
  }

  setPaginationState(paginationState: PState): void {
    this.paginationStateSubject.next(paginationState);
  }

  refreshSelected(): void {
    this.setSelectedItem(this.selectedItemId);
  }

  protected toId(item: T | MaybeId): MaybeId {
    if (typeof item === 'number') {
      return item;
    }
    return item?.id ?? null;
  }

  /**
   * Retourne l'index de l'item
   * Si l'index === -1, renvoi null
   */
  protected getSelectedItemIndex(itemId: MaybeId, paginationState: PState): MaybeId {
    const index = paginationState?.currentPageData?.indexOf(itemId) ?? null;
    return index > -1 ? index : null;
  }

  protected isLastIndex(itemId: MaybeId, paginationState: PState): boolean {
    return this.getSelectedItemIndex(itemId, paginationState) === paginationState?.currentPageData?.length - 1;
  }

  protected isFirstIndex(itemId: MaybeId, paginationState: PState): boolean {
    return this.getSelectedItemIndex(itemId, paginationState) === 0;
  }

  protected getNextNavigation(): Observable<PaginationState> {
    return (
      this.paginationStateSubject
        // On récupère l'observable de la pagination
        .asObservable()
        .pipe(
          // On skip l'état courant
          skip(1),
          // On récupère seulement le prochain état
          take(1)
        )
    );
  }

  protected abstract getById(itemId: MaybeId): Observable<T | null>;
}
